import { GROUPS } from './nav';
import { SECTIONS as VERIFICATION } from '../pages/Verification/VerificationAside';
import { SECTIONS as AFFILIATE } from '../pages/Affiliate/AffiliateAside';
import { SECTIONS as PROFILE } from '../pages/Profile/data';

/**
 * Where the breadcrumb (N84) reads its names. Not a list of its own: each
 * level is the nav row or section tab the trader already sees for it, so a
 * renamed page renames its crumb too.
 *
 * A root is a top-level section — every nav row, plus the pages reached from
 * elsewhere (the account menu, a card, a bell). Its `children` are the
 * section's own tabs, from the same list that section's nav draws.
 */
const NAV_ROOTS = GROUPS.flatMap((group) => group.items).map((item) => ({
  to: item.to,
  label: item.label,
  children: item.items ?? [],
}));

const SECTION_CHILDREN = {
  '/verification': VERIFICATION,
  '/affiliate': AFFILIATE,
};

const ROOTS = [
  ...NAV_ROOTS.map((root) => ({
    ...root,
    children: [...root.children, ...(SECTION_CHILDREN[root.to] ?? [])],
  })),
  // Off the nav — reached from the account menu, the bell, or a card — and
  // named by their own page headings.
  { to: '/profile', label: 'Profile', children: PROFILE },
  { to: '/notifications', label: 'Notifications', children: [] },
  { to: '/divento', label: 'Divento', children: [] },
  { to: '/alpha-pulse', label: 'Alpha Pulse', children: [] },
];

/**
 * Levels below a section's tabs, named by their page headings. Breach
 * Analysis hangs under Account Metrics because that is the only way in.
 */
const LEAVES = {
  '/payout/request': { parent: '/payout', label: 'Request payout' },
  '/competition/all': { parent: '/competition', label: 'Competitions' },
  // Hidden with its tab for now (IdentityVerification.jsx).
  // '/verification/identity/documents': { parent: '/verification/identity', label: 'Document Verification' },
  '/breach-analysis': { parent: '/account-metrics', label: 'Breach Analysis' },
  // Affiliate Marketing's tabs; its Overview is the section row's own page.
  '/affiliate/marketing/codes': { parent: '/affiliate/marketing', label: 'Affiliate Codes' },
  '/affiliate/marketing/payouts': { parent: '/affiliate/marketing', label: 'Commission Payouts' },
};

const under = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

/** The deepest entry whose path the current one is, or starts with. */
const deepest = (pathname, entries) =>
  entries
    .filter((entry) => under(pathname, entry.to))
    .sort((a, b) => b.to.length - a.to.length)[0];

/**
 * The trail for a path: `[{ label, to }]`, top level first, the page itself
 * last. Unknown paths read as the 404 they render.
 */
export function trailFor(pathname) {
  const leaf = LEAVES[pathname];
  const sectionPath = leaf ? leaf.parent : pathname;

  const root = deepest(sectionPath, ROOTS);
  if (!root) return [{ label: 'Page not found', to: pathname }];

  const trail = [{ label: root.label, to: root.to }];
  const child = deepest(sectionPath, root.children);
  // A tab that *is* the section's own page (Payout under Payout &
  // Certificates, Dashboard under Affiliate) still gets its level, so the
  // trail always ends on what the page is called.
  if (child && !(child.to === root.to && child.label === root.label)) {
    trail.push({ label: child.label, to: child.to });
  }
  if (leaf) trail.push({ label: leaf.label, to: pathname });
  return trail;
}

/**
 * Pages with no shell crumb: the competition detail draws its own, richer one
 * (it names the competition, which the shell can't know), and the Affiliate
 * section has none — its own section nav already says where you are.
 */
export const OWN_CRUMB = (pathname) =>
  /^\/competition\/(?!all$)[^/]+$/.test(pathname) || /^\/affiliate(\/|$)/.test(pathname);
